import type { Auth0ContextInterface } from '@auth0/auth0-react';
import {
  PricingStatsMapSchema,
  type TriggerPricingUpdateRequest,
  type VendorPricingStats,
} from '../data/admin';
import { type OnboardingStoreEvents, OnboardingStoreEventsSchema } from '../data/event';
import { type OnboardingOverview, OnboardingOverviewSchema } from '../data/onboarding';
import { createSingleHandler, FetchWrapper } from '../helpers/fetch-wrapper';

const baseUrl = `${import.meta.env.VITE_SERVER_URL}/admin`;

export class AdminService {
  fetchWrapper: FetchWrapper;

  constructor(tokenAccessor: Auth0ContextInterface['getAccessTokenSilently']) {
    this.fetchWrapper = new FetchWrapper(tokenAccessor);
  }

  getPricingStats(): Promise<Record<string, VendorPricingStats>> {
    return this.fetchWrapper
      .get(`${baseUrl}/pricing/stats`)
      .then(createSingleHandler(PricingStatsMapSchema));
  }

  triggerPricingUpdate(request: TriggerPricingUpdateRequest): Promise<void> {
    return this.fetchWrapper.post(`${baseUrl}/pricing/trigger`, request).then(() => {
      // API returns success message, but we don't need it for the mutation
    });
  }

  triggerAllNyslaPricing(): Promise<void> {
    return this.fetchWrapper.post(`${baseUrl}/pricing/trigger-all-nysla`, {}).then(() => {});
  }

  getOnboardingOverview(): Promise<OnboardingOverview> {
    return this.fetchWrapper
      .get(`${baseUrl}/onboarding/overview`)
      .then(createSingleHandler(OnboardingOverviewSchema));
  }

  getOnboardingStoreEvents(
    storeId: string,
    opts: { limit?: number; after?: string } = {}
  ): Promise<OnboardingStoreEvents> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) {
      params.set('limit', String(opts.limit));
    }

    if (opts.after !== undefined) {
      params.set('after', opts.after);
    }

    const url = `${baseUrl}/onboarding/stores/${storeId}/events`;
    const fullUrl = params.size > 0 ? `${url}?${params}` : url;

    return this.fetchWrapper.get(fullUrl).then(createSingleHandler(OnboardingStoreEventsSchema));
  }
}
